import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

import {
  G6_MATERIALS_CRUD,
  DEBTOR_REMINDERS,
  DIPLOMA_SEND_EMAIL,
  DIPLOMA_GENERATION,
  INVOICE_ISSUE,
} from 'core/featureFlags';

import { CAREER_ROLES, FINANCE_PLAN_ROLES } from '../Palette/paletteRoles';

/**
 * Capability booleans for the Group Dashboard, derived from the current user's
 * roles. Hiding a control is a UX affordance only — the backend remains the
 * authority on each mutation/export.
 */
export interface IGroupPermissions {
  /** Admin, FinanceManager, AccountManager (SalesManager: read). */
  canViewFinance: boolean;
  /** Same set as `canViewFinance`. */
  canExportFinance: boolean;
  /** Admin, AccountManager, SalesManager, FinanceManager. */
  canTerminate: boolean;
  /** (Admin, FinanceManager, AccountManager) AND the `DEBTOR_REMINDERS` flag. */
  canRemindDebtors: boolean;
  /**
   * BULK «Выставить фактуры» over the group — issue REAL Fakturownia VAT
   * documents for many students at once. (Admin, FinanceManager,
   * AccountManager) AND the `INVOICE_ISSUE` flag. Flag-gated for the same
   * reason as the single-student action: every created document gets a fiscal
   * number and CANNOT be deleted from the ERP, so it stays off on a
   * half-configured install. The backend is the authority
   * (`FAKTUROWNIA_ACCESS_ROLES`).
   */
  canIssueFaktura: boolean;
  /** (Admin, Methodist, Teacher) AND the `G6_MATERIALS_CRUD` flag. */
  canManageMaterials: boolean;
  /** Attach/replace/delete diplomas: Admin, Methodist, AccountManager. */
  canManageDiplomas: boolean;
  /** `canManageDiplomas` AND the off-by-default `DIPLOMA_SEND_EMAIL` flag. */
  canSendDiplomaEmails: boolean;
  /** `canManageDiplomas` AND the off-by-default `DIPLOMA_GENERATION` flag. */
  canGenerateDiplomas: boolean;
  /**
   * ТЗ 0041 — видеть вкладку «Навыки» группы: Admin, AccountManager,
   * HRManager, Methodist (зеркало `CAREER_ROLES` на бэкенде).
   *
   * 🔴 Прятать вкладку обязательно, а не «желательно». Карточка группы
   * открыта шире карьерного домена, и роль без прав на `careerGroupSkills`
   * получит на клик не отказ, а пятисотку: маршрут и права на запрос — разные
   * оси, и на этом уже обжигались.
   */
  canSeeGroupSkills: boolean;
  /**
   * ТЗ 0075 — кнопка «Выгрузка для P&L» на вкладке «Финансы»: Admin,
   * FinanceManager (`FINANCE_PLAN_ROLES`, зеркало `PNL_EXPORT_ROLES` на
   * бэкенде). Уже, чем `canViewFinance`: аккаунт-менеджеру и продажам вкладка видна,
   * а заказ выгрузки сервер им не даст.
   */
  canExportPnl: boolean;
}

const hasAnyRole = (roles: UserRole[], allowed: UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

/**
 * Reads `userStateVar().me.roles` and maps `UserRole` to capability booleans,
 * mirroring the backend TZ permission notes.
 */
export const useGroupPermissions = (): IGroupPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];

  const canViewFinance = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.FINANCIER,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
  ]);

  const canTerminate = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
    UserRole.FINANCIER,
  ]);

  const canRemindDebtors =
    DEBTOR_REMINDERS &&
    hasAnyRole(roles, [
      UserRole.ADMIN,
      UserRole.FINANCIER,
      UserRole.ACCOUNTANT,
    ]);

  const canIssueFaktura =
    INVOICE_ISSUE &&
    hasAnyRole(roles, [
      UserRole.ADMIN,
      UserRole.FINANCIER,
      UserRole.ACCOUNTANT,
    ]);

  const canManageMaterials =
    G6_MATERIALS_CRUD &&
    hasAnyRole(roles, [UserRole.ADMIN, UserRole.METHODIST, UserRole.TEACHER]);

  const canManageDiplomas = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.METHODIST,
    UserRole.ACCOUNTANT,
  ]);

  // Список берём ОДИН на приложение (`CAREER_ROLES`), а не переписываем
  // рядом: две копии одного правила расходятся молча, и разойтись они успеют
  // раньше, чем кто-нибудь это заметит.
  const canSeeGroupSkills = hasAnyRole(roles, CAREER_ROLES);

  return {
    canViewFinance,
    canExportFinance: canViewFinance,
    canTerminate,
    canRemindDebtors,
    canIssueFaktura,
    canManageMaterials,
    canManageDiplomas,
    canSendDiplomaEmails: DIPLOMA_SEND_EMAIL && canManageDiplomas,
    canGenerateDiplomas: DIPLOMA_GENERATION && canManageDiplomas,
    canSeeGroupSkills,
    canExportPnl: hasAnyRole(roles, FINANCE_PLAN_ROLES),
  };
};
